import { Component, forwardRef, input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subject, takeUntil } from 'rxjs';
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { FormValidatorMessages } from '../../core/enums/forms.enums';
import { BaseInputComponentClass } from '../../core/base-classes/base-input-component-class';

@Component({
  selector: 'ms-amount-input',
  standalone: true,
  imports: [NgxMaskDirective, ReactiveFormsModule],
  templateUrl: './amount-input.component.html',
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmountInput),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AmountInput),
      multi: true
    }
  ]
})
export class AmountInput extends BaseInputComponentClass implements ControlValueAccessor, OnInit, OnDestroy {

  //#region Properties

  /**
   * Off for a host that renders its own message. Two messages for one empty field is noise, and a
   * host that owns the message can also reserve room for it so showing one does not move the layout.
   */
  public showValidationMessages = input<boolean>(true);

  public amountControl = new FormControl<string>('');

  private destroy$ = new Subject<void>();

  //#endregion Properties

  //#region Initialization & Cleanup

  public override ngOnInit(): void {
    super.ngOnInit();

    this.amountControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.onChange(value ?? ''));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //#endregion Initialization & Cleanup

  //#region ControlValueAccessor

  public writeValue(value: string | number | null | undefined): void {
    const normalized = value === null || value === undefined ? '' : String(value);
    this.amountControl.setValue(normalized, { emitEvent: false });
  }

  public override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.amountControl.disable({ emitEvent: false });
    } else {
      this.amountControl.enable({ emitEvent: false });
    }
  }

  //#endregion ControlValueAccessor

  //#region State Getters

  public get isValid(): boolean {
    return this.touched
      && !CommonChecks.isNullOrUndefinedOrEmpty(this.amountControl.value)
      && this.ngControl?.control?.errors == null;
  }

  //#endregion State Getters

  //#region Event Handlers

  public override onBlur(): void {
    this.formatAmount();
    super.onBlur();
  }

  //#endregion Event Handlers

  //#region Validation

  public getErrors(): string[] {
    const errors: string[] = [];
    const controlErrors = this.ngControl?.control?.errors;

    if (controlErrors) {
      if (controlErrors['required']) {
        errors.push(controlErrors['required'].message || FormValidatorMessages.AmountRequired);
      }
      if (controlErrors['validAmount']) {
        errors.push(controlErrors['validAmount'].message || FormValidatorMessages.ChargeScheduleAmountInvalid);
      }
      if (controlErrors['min']) {
        const minValue = controlErrors['min'].min;
        errors.push(`Amount must be at least $${parseFloat(minValue).toFixed(2)}`);
      }
    }

    return errors;
  }

  //#endregion Validation

  //#region Private Methods

  private formatAmount(): void {
    const current = this.amountControl.value;
    if (!current) return;

    const numericValue = parseFloat(current.toString().replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      this.amountControl.setValue(numericValue.toFixed(2));
    } else {
      this.amountControl.setValue('');
    }
  }

  //#endregion Private Methods
}
